import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
    Box,
    Grid,
    useMediaQuery,
    Container,
    Typography,
    Skeleton,
    Fade,
    Button
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import { ref, get } from "firebase/database";
import { useSearchParams } from "react-router-dom";

import { rtdb } from "../services/firebase";
import ProdutosHeader from "../components/ProdutosHeader";
import FiltrosSidebar from "../components/FiltrosSidebar";
import ProductCard from "../components/ProductCard";
import { productMatchesCategoriaFilters } from "../utils/productCategories";

// --- UTILS ---
// Extraído para fora para não ser recriado a cada render
const converterPreco = (precoStr) => {
    if (!precoStr) return 0;
    const limpo = precoStr.toString().replace(/[^\d,.]/g, "");
    const numero = parseFloat(limpo.replace(",", "."));
    return isNaN(numero) ? 0 : numero;
};

// --- CUSTOM HOOK: Lógica de Negócios ---
const useProdutos = () => {
    const [rawProdutos, setRawProdutos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filtros, setFiltros] = useState({
        preco: [0, 5000], // Aumentei o range padrão por segurança
        categorias: [],
        marcas: [],
        promocoes: false,
        emEstoque: false,
    });
    const [ordenacao, setOrdenacao] = useState("relevancia");

    useEffect(() => {
        const fetchProdutos = async () => {
            try {
                const productsRef = ref(rtdb, "products");
                const snapshot = await get(productsRef);

                if (snapshot.exists()) {
                    const data = snapshot.val();
                    const lista = Object.keys(data).map((id) => ({ id, ...data[id] }));
                    setRawProdutos(lista);
                } else {
                    setRawProdutos([]);
                }
            } catch (err) {
                console.error("Erro ao buscar produtos:", err);
                setRawProdutos([]);
            } finally {
                setLoading(false);
            }
        };
        fetchProdutos();
    }, []);

    const maxPreco = useMemo(() => {
        let max = 0;

        rawProdutos.forEach((p) => {
            const preco = p.precos?.isPromo && p.precos?.promo
                ? converterPreco(p.precos.promo)
                : converterPreco(p.precos?.normal);

            if (preco > max) max = preco;
        });

        return Math.ceil(max / 100) * 100 || 1000;
    }, [rawProdutos]);

    useEffect(() => {
        if (maxPreco > 0) {
            setFiltros((prev) => ({
                ...prev,
                preco: [0, maxPreco],
            }));
        }
    }, [maxPreco]);

    // Lógica Unificada e Otimizada com useMemo
    const produtosProcessados = useMemo(() => {
        let processados = [...rawProdutos];

        processados = processados.filter(
            (p) => p.status === "active"
        );

        // 1. Filtros
        processados = processados.filter((p) => {
            const preco = p.precos?.isPromo && p.precos?.promo
                ? converterPreco(p.precos.promo)
                : converterPreco(p.precos?.normal);

            // Preço
            if (preco < filtros.preco[0] || preco > filtros.preco[1]) return false;

            // Categoria
            if (filtros.categorias.length > 0 && !productMatchesCategoriaFilters(p, filtros.categorias)) return false;

            //marca
            if (filtros.marcas?.length > 0 && !filtros.marcas.includes(p.marca)) return false;

            // Promoção
            if (filtros.promocoes && !p.precos?.isPromo) return false;

            // Estoque
            if (filtros.emEstoque) {
                const temEstoque = p.variacoes?.some(v => parseInt(v.quantidade || "0", 10) > 0);
                if (!temEstoque) return false;
            }

            return true;
        });

        // 2. Ordenação
        switch (ordenacao) {
            case "preco_menor":
                processados.sort((a, b) => {
                    const pa = a.precos?.isPromo ? converterPreco(a.precos.promo) : converterPreco(a.precos?.normal);
                    const pb = b.precos?.isPromo ? converterPreco(b.precos.promo) : converterPreco(b.precos?.normal);
                    return pa - pb;
                });
                break;
            case "preco_maior":
                processados.sort((a, b) => {
                    const pa = a.precos?.isPromo ? converterPreco(a.precos.promo) : converterPreco(a.precos?.normal);
                    const pb = b.precos?.isPromo ? converterPreco(b.precos.promo) : converterPreco(b.precos?.normal);
                    return pb - pa;
                });
                break;
            case "novidades":
                processados.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
                break;
            default:
                break;
        }

        return processados;
    }, [rawProdutos, filtros, ordenacao]);

    const limparFiltros = useCallback(() => {
        setFiltros({
            preco: [0, maxPreco],
            categorias: [],
            marcas: [],
            promocoes: false,
            emEstoque: false,
        });
    }, [maxPreco]);

    return {
        produtos: produtosProcessados,
        rawProdutos, // Útil para saber se existem produtos mas o filtro escondeu
        loading,
        filtros,
        setFiltros,
        ordenacao,
        setOrdenacao,
        limparFiltros
    };
};

// --- COMPONENTE PRINCIPAL ---
export default function Produtos() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md")); // Usando breakpoint do tema
    const [drawerOpen, setDrawerOpen] = useState(false);

    const [searchParams] = useSearchParams();

    const [marcasDb, setMarcasDb] = useState([]);

    const {
        produtos,
        rawProdutos,
        loading,
        filtros,
        setFiltros,
        setOrdenacao,
        limparFiltros
    } = useProdutos();

    useEffect(() => {
        const categoriaUrl = searchParams.get("categoria");

        if (categoriaUrl) {
            setFiltros((prev) => ({
                ...prev,
                categorias: [categoriaUrl],
            }));
        }
    }, [searchParams, setFiltros]);

    useEffect(() => {
      async function loadMarcas() {
        const snap = await get(ref(rtdb, "app/brands"));
        if (snap.exists()) {
          const val = snap.val();
          // se vier array:
          if (Array.isArray(val)) setMarcasDb(val);
          // se vier objeto:
          else setMarcasDb(Object.values(val));
        } else {
          setMarcasDb([]);
        }
      }

      loadMarcas();
    }, [searchParams, setFiltros]);

    useEffect(() => {
        const marcaUrl = searchParams.get("marca");

        if (marcaUrl) {
            setFiltros((prev) => ({
                ...prev,
                marcas: [marcaUrl],
            }));
        }
    }, [searchParams, setFiltros]);

    // Renderização do Estado Vazio (Skeleton ou Mensagem)
    const renderContent = () => {
        // 1. Carregando (Skeleton Effect)
        if (loading) {
            return (
                <Grid container spacing={3}>
                    {Array.from(new Array(8)).map((_, index) => (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                            <Box sx={{ pt: 0.5 }}>
                                <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
                                <Skeleton width="60%" height={30} sx={{ mt: 1 }} />
                                <Skeleton width="40%" height={20} />
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            );
        }

        // 2. Sem produtos no banco
        if (rawProdutos.length === 0) {
            return (
                <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="50vh">
                    <Typography variant="h6" color="text.secondary">Nenhum produto cadastrado.</Typography>
                </Box>
            );
        }

        // 3. Filtros muito restritivos (Nenhum resultado)
        if (produtos.length === 0) {
            return (
                <Fade in={true}>
                    <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        minHeight="40vh"
                        textAlign="center"
                        gap={2}
                    >
                        <FilterAltOffIcon sx={{ fontSize: 60, color: 'text.disabled' }} />
                        <Box>
                            <Typography variant="h6" gutterBottom>
                                Nenhum produto encontrado com esses filtros.
                            </Typography>
                            <Typography variant="body2" color="text.primary" paragraph>
                                Tente ajustar a faixa de preço ou categorias.
                            </Typography>
                            <Button
                                variant="outlined"
                                onClick={limparFiltros}
                                sx={{ mt: 1 }}
                            >
                                Limpar Filtros
                            </Button>
                        </Box>
                    </Box>
                </Fade>
            );
        }

        // 4. Lista de Produtos
        return (
            <Fade in={true} timeout={500}>
                <Grid
                    container
                    spacing={isMobile ? 2 : 3}
                    justifyContent={isMobile ? "center" : "flex-start"}
                >
                    {produtos.map((item) => (
                        <Grid
                            item
                            xs={12}
                            sm={6}
                            md={4}
                            lg={3}
                            key={item.id}
                            sx={{
                                display: "flex",
                                justifyContent: "center", // 🔥 centraliza o card
                            }}
                        >
                            <Box
                                sx={{
                                    width: "100%",
                                    maxWidth: { xs: 360, sm: "100%" }, // 🔥 aparência de app
                                }}
                            >
                                <ProductCard product={item} />
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </Fade>
        );
    };

    return (
        <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "#f5f5f5" }}>

            {/* SIDEBAR DESKTOP */}
            {!isMobile && (
                <Box
                    component="aside"
                    sx={{
                        width: 280,
                        flexShrink: 0,
                        borderRight: "1px solid rgba(0,0,0,0.08)",
                        bgcolor: "white",
                        // Sticky sidebar effect
                        position: "sticky",
                        top: 0,
                        height: "100vh",
                        overflowY: "auto"
                    }}
                >
                    <FiltrosSidebar
                        produtos={rawProdutos} // Passa raw para calcular counts se necessário
                        filtrosAtuais={filtros} // Passando estado controlado
                        onFiltersChange={setFiltros}
                        marcasDb={marcasDb}
                    />
                </Box>
            )}

            {/* ÁREA PRINCIPAL */}
            <Box component="main" sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
                <Container maxWidth="xl" sx={{ py: 3, flexGrow: 1 }}>

                    <ProdutosHeader
                        isMobile={isMobile}
                        openDrawer={() => setDrawerOpen(true)}
                        onSortChange={setOrdenacao}
                        totalProdutos={produtos.length} // Passar contagem é uma boa prática de UX
                    />

                    <Box sx={{ mt: 3 }}>
                        {renderContent()}
                    </Box>

                </Container>
            </Box>

            {/* DRAWER MOBILE */}
            {isMobile && (
                <FiltrosSidebar
                    mobile
                    open={drawerOpen}
                    onClose={() => setDrawerOpen(false)}
                    produtos={rawProdutos}
                    filtrosAtuais={filtros}
                    onFiltersChange={setFiltros}
                    marcasDb={marcasDb}
                />
            )}
        </Box>
    );
}